import { z } from 'zod'

import {
  EnumBuilder,
  type NonEmptyArray,
  type ZodInfer,
} from '@mntn-dev/utility-types'

import { ModelFilterSchema } from '../model-filter-schema.ts'
import {
  BidScorecardModelSchema,
  BidScoreSchema,
} from './bid-scoring.models.ts'
import {
  CostMarginPercentSchema,
  DollarsSchema,
  NonEmptyStringSchema,
  TimestampSchema,
} from './property.models.ts'
import {
  AgreementIdSchema,
  BidIdSchema,
  BidUrnSchema,
  OrganizationIdSchema,
  ProjectIdSchema,
  TeamIdSchema,
  UserIdSchema,
} from './unique-id.models.ts'

/**
 * BidStatus
 */
export const [BidStatuses, BidStatusSchema, BidStatusEnum] = EnumBuilder(
  'declined', // The agency has indicated they do not want to submit a bid
  'draft', // The agency is preparing the bid but has not yet submitted it
  'submitted', // The bid has been officially submitted for consideration
  'disqualified', // The bid was not submitted on time
  'accepted', // The bid was accepted and the contract is being finalized
  'rejected', // The bids that were specifically rejected by the brand
  'unsuccessful' // The bids that were not specifically rejected, but did not win the bid
)
export type BidStatus = ZodInfer<typeof BidStatusSchema>

export const BrandBidStatuses: NonEmptyArray<BidStatus> = [
  'accepted',
  'rejected',
  'submitted',
]

/**
 * Postgres max value for integer is 2147483647 which we store as cents
 * The max amount of dollars that can be represented is $21,474.836.47
 * The amountPlusMargin is twice as large as the amount, so we're staying about half this size to be safe.
 */
export const BidAmountMax = 10_000_000
export const BidAmountSchema = DollarsSchema(BidAmountMax)

const BidSignatureSchema = z.object({
  userId: UserIdSchema,
  timestamp: TimestampSchema,
  agreements: AgreementIdSchema.array(),
})
/**
 * BidDomainSelectModel
 */
export const BidDomainSelectModelSchema = z
  .object({
    bidId: BidIdSchema,
    bidUrn: BidUrnSchema,
    projectId: ProjectIdSchema,
    agencyOrganizationId: OrganizationIdSchema,
    agencyTeamId: TeamIdSchema,
    status: BidStatusSchema,
    revision: z.int().nonnegative(),
    amount: BidAmountSchema,
    costMarginPercent: CostMarginPercentSchema,
    amountPlusMargin: DollarsSchema(),
    pitch: NonEmptyStringSchema(),
    score: BidScoreSchema,
    signatures: z
      .object({
        brand: BidSignatureSchema,
        maker: BidSignatureSchema,
      })
      .partial(),
    submittedAmount: BidAmountSchema,
  })
  .partial({
    amount: true,
    amountPlusMargin: true,
    pitch: true,
    score: true,
    submittedAmount: true,
  })

export type BidDomainSelectModel = ZodInfer<typeof BidDomainSelectModelSchema>

/**
 * BidDomainInsertModel
 */
export const BidDomainInsertModelSchema = BidDomainSelectModelSchema.pick({
  bidId: true,
  projectId: true,
  agencyOrganizationId: true,
  agencyTeamId: true,
  costMarginPercent: true,
})
  .extend({ status: BidStatusSchema.extract(['draft', 'declined']) })
  .partial({ bidId: true })

export type BidDomainInsertModel = ZodInfer<typeof BidDomainInsertModelSchema>

/**
 * BidDomainUpdateModel
 */
export const BidDomainUpdateModelSchema = BidDomainSelectModelSchema.pick({
  status: true,
  revision: true,
  agencyTeamId: true,
})
  .extend({
    amount: BidDomainSelectModelSchema.shape.amount.nullable(),
    pitch: BidDomainSelectModelSchema.shape.pitch.nullable(),
    signatures: BidDomainSelectModelSchema.shape.signatures
      .extend({
        brand: BidSignatureSchema.nullable(),
        maker: BidSignatureSchema.nullable(),
      })
      .partial(),
    score: BidDomainSelectModelSchema.shape.score.nullable(),
    scorecard: BidScorecardModelSchema.nullable(),
    submittedAmount:
      BidDomainSelectModelSchema.shape.submittedAmount.nullable(),
  })
  .partial()

export type BidDomainUpdateModel = ZodInfer<typeof BidDomainUpdateModelSchema>

/**
 * BidDomainSelectModelFilters
 */
export const BidDomainSelectModelFiltersSchema = z
  .object({
    bidId: ModelFilterSchema(BidIdSchema),
    bidUrn: ModelFilterSchema(BidUrnSchema),
    projectId: ModelFilterSchema(ProjectIdSchema),
    agencyOrganizationId: ModelFilterSchema(OrganizationIdSchema),
    agencyTeamId: ModelFilterSchema(TeamIdSchema),
    status: ModelFilterSchema(BidStatusSchema),
  })
  .partial()

export type BidDomainSelectModelFilters = ZodInfer<
  typeof BidDomainSelectModelFiltersSchema
>
